import React, { useCallback, useEffect, useState } from 'react';
import ReactDom from 'react-dom';
import { findOrCreateAnnotationLayer } from '../../lib/pdfjs-dom';
import { groupAnnotationsByPage } from '../../lib/util';
import { MouseSelection } from './MouseSelection';
import { PDFViewer } from 'pdfjs-dist/web/pdf_viewer';
import { PDFDocumentProxy } from 'pdfjs-dist';
import { Label } from '../../types/label';
import { Position } from '../../types/result';
import { HighlightedWord } from './HighlightedWord';

type Props = {
  newLabelAllowed?: boolean;
  readOnly?: boolean;
  viewer: PDFViewer;
  container: null | HTMLElement;
  pdfDocument: PDFDocumentProxy;
  highlightedWords: Label[];
  selectedWord: null | Label;
  editWord: null | Label;
  onAddLabel: (position: Position, page: { number: number }, name: string) => any;
  onEditLabel: (position: Position, page: { number: number }) => any;
  onRemoveLabel: (label: Label) => any;
  onReEditLabel: (label: Label) => any;
  onResetRemovedLabel: (label: Label) => any;
  onEditThisLabel: (label: Label, position: Position, page: { number: number }) => any;
  publicDemo?: boolean;
};

export const PdfWordsLayer = ({
  viewer,
  container,
  pdfDocument,
  highlightedWords,
  selectedWord,
  editWord,
  onAddLabel,
  onEditLabel,
  onRemoveLabel,
  onReEditLabel,
  onResetRemovedLabel,
  onEditThisLabel,
  newLabelAllowed,
  readOnly,
  publicDemo,
}: Props) => {
  // Only allow resize when user is holding down shift-key
  const [allowResize, setAllowResize] = useState<boolean>(false);

  const renderBoxes = useCallback(
    ({
      pdfDocument,
      viewer,
      selectedWord,
      highlightedWords,
    }: {
      pdfDocument: PDFDocumentProxy;
      viewer: PDFViewer;
      selectedWord: null | Label;
      highlightedWords: Label[];
    }) => {
      if (!viewer) {
        return;
      }

      const highlightedWordsByPage: {
        [page: string]: Label[];
      } = groupAnnotationsByPage(highlightedWords);

      for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber++) {
        const annotationLayer = findOrCreateAnnotationLayer(viewer, pageNumber);
        if (annotationLayer) {
          ReactDom.render(
            <div className="z-10">
              {(highlightedWordsByPage[String(pageNumber)] || []).map(highlightedWord => {
                return (
                  <HighlightedWord
                    key={highlightedWord.id}
                    viewer={viewer}
                    highlightedWord={highlightedWord}
                    pageNumber={pageNumber}
                    selectedWord={selectedWord}
                    onEditThisLabel={onEditThisLabel}
                    allowResize={allowResize}
                    publicDemo={publicDemo}
                  />
                );
              })}
            </div>,
            annotationLayer
          );
        }
      }
    },
    //eslint-disable-next-line
    [allowResize]
  );

  useEffect(() => {
    renderBoxes({ pdfDocument, viewer, selectedWord, highlightedWords });
  }, [pdfDocument, viewer, renderBoxes, selectedWord, highlightedWords]);

  useEffect(() => {
    console.log(allowResize);
  }, [allowResize]);

  // add event listeners to switch 'allowResize' from false to true when holding down shift-key
  // -> also remove event listeners

  const updateAllowResize = (evt: KeyboardEvent, value: boolean) => {
    if (evt.code === 'ShiftLeft' || evt.code === 'ShiftRight') {
      setAllowResize(value);
    }
  };

  useEffect(() => {
    const allowResize = (evt: KeyboardEvent) => {
      updateAllowResize(evt, true);
    };

    window.addEventListener('keydown', allowResize);

    return () => {
      window.removeEventListener('keydown', allowResize);
    };
  }, []);

  useEffect(() => {
    const unAllowResize = (evt: KeyboardEvent) => {
      updateAllowResize(evt, false);
    };

    window.addEventListener('keyup', unAllowResize);

    return () => {
      window.removeEventListener('keyup', unAllowResize);
    };
  }, []);

  return (
    <div>
      <MouseSelection
        viewer={viewer}
        container={container}
        onEditLabel={onEditLabel}
        onAddLabel={onAddLabel}
        onRemoveLabel={onRemoveLabel}
        onReEditLabel={onReEditLabel}
        onResetRemovedLabel={onResetRemovedLabel}
        labelEdit={editWord}
        setSelection={() => {}}
        labels={highlightedWords.map(word => word.name)}
        shouldStart={() => !readOnly && (newLabelAllowed || !!editWord)}
        allowResize={allowResize}
      />
    </div>
  );
};
